import { useContext } from "react";
import { AccountFormContext } from "@shared/features/common/AccountForm.context";
import Col from "antd/es/col";
import Row from "antd/es/row";
import { ICON_TYPES } from "@shared/common/constants";
import Text from "common/Typography/Text";
import React from "react";
import styled, { css } from "styled-components";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants.ts";
import Flex from "common/Boxes/Flex";
import IconWithFallback from "Global/IconWithFallback";
import {
  EXCHANGE_ICON_MARGIN,
  EXCHANGE_ICON_SIZE,
} from "@shared/features/onboarding/constants";
import { AccountRowProps } from "@shared/features/onboarding/types";

const AccountRow = ({
  account,
  currency = "USD",
  disabled,
  total,
  available,
  isBot,
  totalSignals,
  spans,
  isOnboarding,
}: AccountRowProps) => {
  const { exchangesData } = useContext(AccountFormContext) || {};
  const exchangeDisplay =
    exchangesData?.find((exchange) => exchange.exchange === account.exchange)
      ?.exchange_display || account.exchange;
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);

  return (
    <Row>
      <Col span={spans[0]} style={{ paddingRight: 12 }}>
        <Flex $justifyContent="start">
          <IconWithFallback
            iconType={ICON_TYPES.EXCHANGE}
            coinOrExchangeName={account.exchange}
            iconStyle={{
              width: EXCHANGE_ICON_SIZE,
              height: EXCHANGE_ICON_SIZE,
              marginRight: EXCHANGE_ICON_MARGIN,
            }}
          />
          <Name
            $isTwoLinesEllipsis={true}
            $isMobile={isMobile}
            $disabled={disabled}
          >
            {isOnboarding ? exchangeDisplay : account.accountName}
          </Name>
        </Flex>
      </Col>
      {!isBot && (
        <Col span={spans[1]}>
          <Signals $isMobile={isMobile}>{totalSignals}</Signals>
        </Col>
      )}
      <Col span={spans[isBot ? 1 : 2]}>
        <AmountWrapper $isMobile={isMobile}>
          {disabled ? (
            <Text type="secondary">-</Text>
          ) : (
            <>
              <Total $isMobile={isMobile} $disabled={disabled}>
                {total?.toLocaleString()} {currency}
              </Total>
              <Available $disabled={disabled}>
                ({available?.toLocaleString()} {currency})
              </Available>
            </>
          )}
        </AmountWrapper>
      </Col>
    </Row>
  );
};

export default AccountRow;

const textSizeStyle = css<{ $isMobile?: boolean }>`
  font-size: ${(p) =>
    p.$isMobile
      ? p.theme.typography.fontSizes.S
      : p.theme.typography.fontSizes.M};
`;

const textColorStyle = css<{ $disabled: boolean }>`
  color: ${(p) =>
    p.$disabled ? p.theme.colors.disabledText : p.theme.colors.primaryText};
`;

const Name = styled(Text)<{ $isMobile: boolean; $disabled: boolean }>`
  ${textSizeStyle};
  ${textColorStyle};
`;
const Signals = styled(Text)<{ $isMobile?: boolean }>`
  ${textSizeStyle};
`;
const AmountWrapper = styled(Flex)<{ $isMobile: boolean }>`
  flex-direction: ${(p) => (p.$isMobile ? "column" : "row")};
  align-items: ${(p) => (p.$isMobile ? "end" : "center")};
  justify-content: flex-end;
  flex-wrap: wrap;
  column-gap: 4px;
`;
const Total = styled(Text)<{ $isMobile: boolean; $disabled: boolean }>`
  ${textSizeStyle};
  ${textColorStyle};
  ${(p) =>
    p.$isMobile &&
    `
  margin-bottom: 4px;
  `};
  text-align: right;
`;
const Available = styled(Text).attrs({ type: "secondary" })<{
  $disabled: boolean;
}>`
  ${textColorStyle};
  text-align: right;
  font-size: ${(p) => p.theme.typography.fontSizes.S};
`;
